// Certificate of Training Completion — the audit artifact an HFRD/GAPP surveyor
// actually wants to see (not just a DB row): staff name, module, linked policy
// citation, completion date, typed signature, record ID. One PDF per completed
// staff_training_records row, filed to Drive under Staff Records/{name}/Compliance.
import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { pdf } from '@react-pdf/renderer';
import { styles, COLORS, Letterhead, ensureCursiveFont } from './pdfPrimitives';

function TrainingCertificateDoc({ data }) {
  ensureCursiveFont();
  const { staffName, moduleNumber, moduleTitle, linkedPolicy, completedAt, signatureRef, recordId } = data;
  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <Letterhead />
        <View style={{ marginTop: 24, alignItems: 'center' }}>
          <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 18, color: COLORS.navy }}>Certificate of Training Completion</Text>
          <Text style={{ fontSize: 9, color: COLORS.mid, marginTop: 8 }}>{moduleNumber} — {moduleTitle}</Text>
        </View>

        <View style={{ marginTop: 32, alignItems: 'center' }}>
          <Text style={{ fontSize: 9, color: COLORS.mid }}>This certifies that</Text>
          <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 16, color: COLORS.ink, marginTop: 6, marginBottom: 6 }}>{staffName}</Text>
          <Text style={{ fontSize: 9, color: COLORS.mid, textAlign: 'center' }}>has completed the training module listed above{'\n'}in accordance with A Heart For Care's Policies and Procedures Manual{linkedPolicy ? ` (${linkedPolicy})` : ''}.</Text>
        </View>

        <View style={{ marginTop: 40, alignItems: 'center' }}>
          <Text style={{ fontFamily: 'Signature', fontSize: 18, lineHeight: 1.0, color: COLORS.sign }}>{signatureRef}</Text>
          <View style={{ borderTopWidth: 0.8, borderTopColor: '#000', width: 220, marginTop: -1 }} />
          <Text style={{ fontSize: 8, color: '#000', marginTop: 2 }}>Signature on file — typed, no-login training link</Text>
        </View>

        <View style={{ marginTop: 40, flexDirection: 'row', justifyContent: 'space-between' }}>
          <View>
            <Text style={styles.fieldLabel}>COMPLETED</Text>
            <Text style={styles.fieldValue}>{completedAt}</Text>
          </View>
          <View>
            <Text style={styles.fieldLabel}>RECORD ID</Text>
            <Text style={{ ...styles.fieldValue, fontSize: 7 }}>{recordId}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}

export async function renderTrainingCertificateBlob(data) {
  return pdf(<TrainingCertificateDoc data={data} />).toBlob();
}

// One consolidated certificate per COURSE (e.g. "Core Compliance Training"),
// listing every section and its individual completion date/signature — this is
// what actually gets issued, not one certificate per section. Matches how real
// LMS platforms (Relias, CareAcademy) certify at the course level.
function CourseCertificateDoc({ data }) {
  ensureCursiveFont();
  const { staffName, courseTitle, courseDescription, completedAt, signatureRef, courseRecordId, sections } = data;
  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <Letterhead />
        <View style={{ marginTop: 20, alignItems: 'center' }}>
          <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 18, color: COLORS.navy }}>Certificate of Training Completion</Text>
          <Text style={{ fontSize: 10, color: COLORS.ink, marginTop: 8, fontFamily: 'Helvetica-Bold' }}>{courseTitle}</Text>
          {courseDescription ? <Text style={{ fontSize: 8, color: COLORS.mid, marginTop: 2, textAlign: 'center', maxWidth: 420 }}>{courseDescription}</Text> : null}
        </View>

        <View style={{ marginTop: 20, alignItems: 'center' }}>
          <Text style={{ fontSize: 9, color: COLORS.mid }}>This certifies that</Text>
          <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 16, color: COLORS.ink, marginTop: 6, marginBottom: 6 }}>{staffName}</Text>
          <Text style={{ fontSize: 9, color: COLORS.mid, textAlign: 'center' }}>has completed all sections of the course listed above{'\n'}in accordance with A Heart For Care's Policies and Procedures Manual.</Text>
        </View>

        <View style={{ marginTop: 20 }}>
          <Text style={styles.sectionHdr}>Sections completed</Text>
          {(sections || []).map((s, i) => (
            <View key={i} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 3, borderBottomWidth: 0.5, borderBottomColor: COLORS.border }}>
              <Text style={{ fontSize: 8.5, color: COLORS.ink }}>{s.moduleNumber} — {s.title}</Text>
              <Text style={{ fontSize: 8, color: COLORS.mid }}>{s.completedAt}</Text>
            </View>
          ))}
        </View>

        <View style={{ marginTop: 28, alignItems: 'center' }}>
          <Text style={{ fontFamily: 'Signature', fontSize: 18, lineHeight: 1.0, color: COLORS.sign }}>{signatureRef}</Text>
          <View style={{ borderTopWidth: 0.8, borderTopColor: '#000', width: 220, marginTop: -1 }} />
          <Text style={{ fontSize: 8, color: '#000', marginTop: 2 }}>Signature on file — typed, no-login training link</Text>
        </View>

        <View style={{ marginTop: 24, flexDirection: 'row', justifyContent: 'space-between' }}>
          <View>
            <Text style={styles.fieldLabel}>COURSE COMPLETED</Text>
            <Text style={styles.fieldValue}>{completedAt}</Text>
          </View>
          <View>
            <Text style={styles.fieldLabel}>RECORD ID</Text>
            <Text style={{ ...styles.fieldValue, fontSize: 7 }}>{courseRecordId}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}

export async function renderCourseCertificateBlob(data) {
  return pdf(<CourseCertificateDoc data={data} />).toBlob();
}

export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
